import { useCallback, useEffect, useState } from 'react';

import { iPublicationClient__API } from '../apiSchema';
import { getApiLink } from '../api';

const HTML_CONTENT_START_MARKER = '```html';
const HTML_CONTENT_END_MARKER = '```';
// reportpro-api's SearchStream appends `<!--ds_ai_sources:{"sources":[...]}-->`
// as the last chunk of the streaming response (ALPS-440). Each source row
// carries the hydrated `iPublicationClient__API` shape (rich tile
// metadata: thumbnail, files, hasAccess, ...) plus the `pages` array of
// pages the model cited. Entries for articles missing from the DB still
// arrive with at least `id`, `title`, `createDate`, `pages` populated.
const SOURCES_SENTINEL_RE = /<!--ds_ai_sources:([\s\S]*?)-->/;

export type iStreamSource = Partial<iPublicationClient__API> & {
    id: number;
    pages?: number[];
};

interface iUseStreamResult {
    isLoading: boolean;
    htmlContent: string;
    sources: iStreamSource[];
    error: Error | null;
    statusCode: number | null;
    isSuccess: boolean;
}

interface iStreamProcessorParams {
    reader: ReadableStreamDefaultReader<Uint8Array>;
    onHtmlContent: (html: string) => void;
    onComplete: (sources: iStreamSource[]) => void;
    onError: (error: Error) => void;
}

function getSearchStreamRequest(query: string, controller: AbortController): Promise<Response> {
    return fetch(getApiLink('PublicationClient/SearchStream', { query }), {
        method: 'POST',
        signal: controller.signal,
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            Accept: 'text/event-stream'
        }
    });
}

export default function useSearchResultsStream(query: string, shouldFetchStream = true): iUseStreamResult {
    const [isLoading, setIsLoading] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [htmlContent, setHtmlContent] = useState('');
    const [sources, setSources] = useState<iStreamSource[]>([]);
    const [error, setError] = useState<Error | null>(null);
    const [statusCode, setStatusCode] = useState<number | null>(null);

    const fetchStream = useCallback(async (controller: AbortController) => {
        if (!query) {
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setHtmlContent('');
        setSources([]);
        setStatusCode(null);
        setError(null);
        setIsSuccess(false);

        try {
            const response = await getSearchStreamRequest(query, controller);
            setStatusCode(response.status);

            if (!response.ok) {
                throw new Error('Stream API Error');
            }

            const reader = response.body?.getReader();
            if (!reader) {
                throw new Error('Stream reader could not be created');
            }

            await processStreamData({
                reader,
                onHtmlContent: (html) => setHtmlContent(html),
                onComplete: (sources) => {
                    setSources(sources);
                    setIsLoading(false);
                    setIsSuccess(true);
                },
                onError: (err) => {
                    setError(err);
                    setIsSuccess(false);
                    setIsLoading(false);
                }
            });
        } catch (err) {
            if (controller.signal.aborted) return;
            setError(err instanceof Error ? err : new Error(String(err)));
            setIsLoading(false);
        }
    }, [query]);

    useEffect(() => {
        const controller = new AbortController();

        if (shouldFetchStream) {
            void fetchStream(controller);
        }

        return () => {
            controller.abort();
        };
    }, [fetchStream, shouldFetchStream]);

    return {
        isLoading,
        htmlContent,
        sources,
        error,
        statusCode,
        isSuccess
    };
}

function extractSourcesFromSentinel(rawResponse: string): iStreamSource[] {
    const match = rawResponse.match(SOURCES_SENTINEL_RE);
    if (!match) return [];
    try {
        const payload = JSON.parse(match[1]);
        if (payload && Array.isArray(payload.sources)) {
            return payload.sources as iStreamSource[];
        }
    } catch {
        // pass
    }
    return [];
}

function stripSourcesSentinel(rawResponse: string): string {
    return rawResponse.replace(SOURCES_SENTINEL_RE, '');
}

/**
 * Extracts HTML content from a raw response string that contains
 * markdown-formatted HTML code blocks. If the response doesn't contain
 * the HTML marker but is longer than the marker length, treats the entire
 * response as HTML content.
 */
function extractHtmlContent(rawResponse: string): string {
    // If response is empty, return empty string
    if (!rawResponse) return '';

    // Extract content between start and end markers
    const htmlStartIndex = rawResponse.indexOf(HTML_CONTENT_START_MARKER);

    // If no start marker found and response is longer than marker length,
    // treat entire response as HTML content
    if (htmlStartIndex === -1) {
        return rawResponse.length > HTML_CONTENT_START_MARKER.length ? repairBrokenHtmlTags(rawResponse) : '';
    }

    const contentStartIndex = htmlStartIndex + HTML_CONTENT_START_MARKER.length;
    let htmlContent = rawResponse.substring(contentStartIndex);

    const closingBackticksIndex = htmlContent.indexOf(HTML_CONTENT_END_MARKER);
    if (closingBackticksIndex > -1) {
        htmlContent = htmlContent.substring(0, closingBackticksIndex);
    }

    return repairBrokenHtmlTags(htmlContent);
}

async function processStreamData({ reader, onHtmlContent, onComplete, onError }: iStreamProcessorParams): Promise<void> {
    let rawResponse = '';

    try {
        while (true) {
            const { done, value } = await reader.read();

            if (done) {
                const sources = extractSourcesFromSentinel(rawResponse);
                const finalHtmlContent = extractHtmlContent(stripSourcesSentinel(rawResponse));
                onHtmlContent(finalHtmlContent);
                onComplete(sources);
                break;
            }

            const chunk = new TextDecoder().decode(value);
            rawResponse += chunk;

            onHtmlContent(extractHtmlContent(rawResponse));
        }
    } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
            return;
        }
        onError(err instanceof Error ? err : new Error(String(err)));
    }
}

/**
 * Repairs broken HTML tags in stream content that might have been split across chunks.
 * Handles malformed opening tags, closing tags, and self-closing tags by removing
 * extra whitespace and ensuring proper tag syntax.
 */
function repairBrokenHtmlTags(htmlContent: string): string {
    // Fix broken opening tags (< tag)
    htmlContent = htmlContent.replace(/\s*<\s+(\w+)/g, '<$1');

    // Fix broken closing tags (</ tag)
    htmlContent = htmlContent.replace(/\s*<\/\s+(\w+)/g, '</$1');

    // Fix broken self-closing tags (< img/)
    htmlContent = htmlContent.replace(/\s*<\s+(\w+)\s*\/>/g, '<$1/>');

    return htmlContent;
}
